import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { http } from '@/helpers/http'
import { track } from '@/helpers/mixpanelDes'
import { amazonCoverDimensions } from '@/constants/coverDimensions'
import { useDraftsStore } from './drafts'
import { useEditorStore } from '@/stores/editor'
import { getPageFormatByName } from '@/helpers/pageFormat'
import { normalizePageNumbering } from '@/helpers/pageNumbering'
import { documentFonts } from '@/constants/print-book-fonts'
import { useUserStore } from '@/stores/user.ts'
import { useAmazonCoversStore } from '@/stores/amazon-covers.ts'
import { useLoaderStore } from '@/stores/loader'
import { useTranscriptionStore } from '@/stores/transcription'
import { downloadBlob } from '@/helpers/download'
import { renderFirstPage, tiptapPagesFirstPage } from '@/helpers/firstPageThumbnail'
import type { TDropdownOption } from '@/types/dropdown'
import type { PageFormat } from '@tiptap-pro/extension-pages'
import type {
  TPrintBookFont,
  TPrintBookStepMap,
  TPrintBookPageNumbering,
} from '@/types/print-books'
import type { TAmazonCoverSize } from '@/types/cover'
import type { TDraftThumbnail } from '@/types/drafts'
import type { Editor } from '@tiptap/core'

const printBookStepPrefixes = [
  '/docs/print-book/',
  '/cover-editor/',
  '/cover-editor/print-book-preview/',
  '/cover-editor/print-book-download/',
  '/creator/printbook',
]

function isValidPrintBookStepUrl(url: string | null | undefined): url is string {
  if (!url || url === '/') {
    return false
  }

  return printBookStepPrefixes.some((prefix) => url.startsWith(prefix))
}

export const usePrintBooksStore = defineStore('print-books', () => {
  const drafts = useDraftsStore()
  const users = useUserStore()
  const covers = useAmazonCoversStore()
  const editor = useEditorStore()
  const sizeUnit = ref<TDropdownOption | undefined>()
  const showAllDimensions = ref<boolean>(false)

  const router = useRouter()

  const stepUrlMap = useLocalStorage<TPrintBookStepMap>('print-book-step-urls', {})

  const sizeUnitOptions: TDropdownOption[] = [
    { name: 'mm', label: 'Millimeters (mm)' },
    { name: 'in', label: 'Inches (in)' },
  ]

  async function toDataURL(url: string, signal?: AbortSignal): Promise<string | null> {
    try {
      const connector = url.includes('?') ? '&' : '?'
      const fetchUrl = `${url}${connector}t=${new Date().getTime()}`

      const response = await fetch(fetchUrl, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'omit',
        signal,
      })

      if (!response.ok) throw new Error(`Status: ${response.status}`)

      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (e) {
      console.error('Error (CORS/Network):', url, e)
      return null
    }
  }

  async function getPDFv2Data(
    draftId: number,
    signal?: AbortSignal,
  ): Promise<{ status: string; url: string; filename: string; pages?: string[] }> {
    const response = await fetch(`/print_books/get_pdf_v2/${draftId}`, {
      method: 'GET',
      signal,
    })

    if (!response.ok) {
      throw new Error(`Failed to generate PDF (${response.status})`)
    }

    return (await response.json()) as { status: string; url: string; filename: string; pages?: string[] }
  }

  async function getPDF(draftId: number): Promise<void> {
    const res = await getPDFv2Data(draftId)

    if (res?.status !== 'success' || !res?.url) {
      throw new Error('Failed to generate PDF')
    }

    const fileRes = await fetch(res.url)
    if (!fileRes.ok) {
      throw new Error(`File fetch failed: ${fileRes.status}`)
    }

    const blob = await fileRes.blob()
    downloadBlob(blob, res.filename)
  }

  function startPdfRequestController(): AbortController {
    if (pdfRequestAbortController) {
      pdfRequestAbortController.abort()
    }

    const controller = new AbortController()
    pdfRequestAbortController = controller
    return controller
  }

  async function preparePDF(draftId: number) {
    const controller = startPdfRequestController()

    let res: { status: string; url: string; filename: string; pages?: string[] }
    try {
      res = await getPDFv2Data(draftId, controller.signal)
    } finally {
      if (pdfRequestAbortController === controller) {
        pdfRequestAbortController = null
      }
    }

    if (res?.status !== 'success' || !res?.url) {
      throw new Error('Failed to generate PDF')
    }
    pages.value = res.pages ?? []
    return res
  }

  async function getCachedPages(draftId: number): Promise<boolean> {
    const controller = startPdfRequestController()

    let res: { cached: boolean; pages: string[] }
    try {
      res = await http.get<{ cached: boolean; pages: string[] }>(
        `/print_books/get_pdf_pages/${draftId}`,
        undefined,
        false,
        controller.signal,
      )
    } finally {
      if (pdfRequestAbortController === controller) {
        pdfRequestAbortController = null
      }
    }

    if (res?.cached !== true) {
      return false
    }
    pages.value = res.pages
    return true
  }

  async function initSizeUnit() {
    let name
    const valueFromSettings = await users.getExtraSettingByKey(users.userSettingLabels.PAGE_SIZE_UNIT)

    if (!valueFromSettings?.length) {
      if (navigator.language === 'en-US') {
        name = 'in'
      } else {
        name = 'mm'
      }
    } else {
      name = valueFromSettings
    }

    sizeUnit.value = sizeUnitOptions.find((o) => o.name === name) ?? sizeUnitOptions[0]!
    if (!valueFromSettings?.length) {
      await users.saveExtraSetting(users.userSettingLabels.PAGE_SIZE_UNIT, name)
    }
  }

  const pages = ref<string[]>([])
  const loader = useLoaderStore()
  let pdfRequestAbortController: AbortController | null = null
  const inFlightThumbnailGenerations = new Map<number, Promise<TDraftThumbnail | null>>()

  function getPagesCount(): number {
    const editorElement = editor.getEditor()
    if (editorElement) {
      return editorElement.view.dom.querySelectorAll('.tiptap-page-break').length - 1
    }
    return 0
  }

  async function onSizeUnitChange(newUnit: TDropdownOption) {
    const selected = selectedDimensionOption.value

    if (!selected || !newUnit) return
    sizeUnit.value = newUnit

    loader.show('Saving page size unit setting...')
    await users.saveExtraSetting(users.userSettingLabels.PAGE_SIZE_UNIT, newUnit.name ?? 'mm')
    loader.hide()
  }

  function cancelPageGeneration() {
    if (pdfRequestAbortController) {
      pdfRequestAbortController.abort()
      pdfRequestAbortController = null
    }
  }

  async function setCover(coverId: number, oldCoverId?: number): Promise<boolean> {
    if (!drafts.draft?.print_book || !drafts.draft?.id) {
      return false
    }
    try {
      const res = await http.post<{ result: boolean }>('/print_books/set_cover', {
        coverId,
        draftId: drafts.draft.id,
      })
      if (!res?.result) {
        throw new Error(`Failed to create print book from draft`)
      }
      drafts.draft.print_book.amazon_cover_id = coverId

      if (oldCoverId) {
        await covers.remove(oldCoverId)
      }
      return true
    } catch {
      throw new Error('Failed to set cover')
    }
  }

  async function createFromDraft(draftId: number): Promise<number> {
    const transcriptions = useTranscriptionStore()
    const draft = drafts.draft?.id === draftId ? drafts.draft : null
    const content = transcriptions.isTranscriptionType(draft?.type)
      ? transcriptions.draftToProjectContent(draft, editor.getEditor())
      : null

    try {
      const res = await http.post<{ id: number }>('/print_books/create_from_draft', {
        draftId,
        ...(content ? { content } : {}),
      })

      if (!res?.id) {
        throw new Error('Failed to create print book')
      }

      drafts.invalidate('printbooks')
      track('print-book-created-from-draft', { draftId })
      return res.id
    } catch {
      track('print-book-create-from-draft-failed', { draftId })
      throw new Error('Failed to create print book')
    }
  }

  const dimensionOptions = computed<TAmazonCoverSize[]>(() =>
    showAllDimensions.value ? amazonCoverDimensions : amazonCoverDimensions.filter((e) => e.popular),
  )

  const selectedDimensionOption = ref<TAmazonCoverSize>(
    amazonCoverDimensions.find((o) => o.name === '152_228') ?? dimensionOptions.value[0]!,
  )

  const setPageFormat = (pageFormat: PageFormat) => {
    const printBook = drafts.draft?.print_book
    if (!printBook?.settings) return

    printBook.settings.pageFormat = pageFormat
    if (typeof pageFormat === 'string') {
      return
    }
    printBook.page_width = pageFormat.width
    printBook.page_height = pageFormat.height
  }

  watch(
    () => drafts.draft?.print_book?.settings,
    (settings) => {
      if (!settings) return

      let parsed = settings
      if (typeof settings === 'string') {
        try {
          parsed = JSON.parse(settings)
        } catch {
          return
        }
      }

      const pf = (parsed as { pageFormat?: PageFormat | { id: string } })?.pageFormat
      const savedId = pf && typeof pf === 'object' ? (pf as { id: string }).id : (pf as string)
      if (!savedId) return

      const option = amazonCoverDimensions.find((o) => o.name === savedId)
      if (option && option.name !== selectedDimensionOption.value.name) {
        selectedDimensionOption.value = option
      }
    },
    { immediate: true, deep: true },
  )

  const selectedPagesDimension = computed(() => {
    return {
      pageFormat: getPageFormatByName(selectedDimensionOption.value.name),
    }
  })

  const defaultTextSizes = { h1: 34, h2: 24, h3: 20, h4: 16, body: 16 }

  const textSizes = computed(() => ({
    h1: drafts.draft?.print_book?.settings?.textSizes?.h1 ?? defaultTextSizes.h1,
    h2: drafts.draft?.print_book?.settings?.textSizes?.h2 ?? defaultTextSizes.h2,
    h3: drafts.draft?.print_book?.settings?.textSizes?.h3 ?? defaultTextSizes.h3,
    h4: drafts.draft?.print_book?.settings?.textSizes?.h4 ?? defaultTextSizes.h4,
    body: drafts.draft?.print_book?.settings?.textSizes?.body ?? defaultTextSizes.body,
  }))

  function setTextSize(level: 'h1' | 'h2' | 'h3' | 'h4' | 'body', val: number) {
    const pb = drafts.draft?.print_book

    if (!pb?.settings) return
    if (!pb.settings.textSizes) pb.settings.textSizes = { ...defaultTextSizes }

    pb.settings.textSizes[level] = val
  }

  function resetTextSizes() {
    const pb = drafts.draft?.print_book

    if (!pb?.settings) return
    pb.settings.textSizes = { ...defaultTextSizes }
  }

  const isTextSizesModified = computed(
    () =>
      textSizes.value.h1 !== defaultTextSizes.h1 ||
      textSizes.value.h2 !== defaultTextSizes.h2 ||
      textSizes.value.h3 !== defaultTextSizes.h3 ||
      textSizes.value.h4 !== defaultTextSizes.h4 ||
      textSizes.value.body !== defaultTextSizes.body,
  )

  const documentFont = computed<TPrintBookFont | null>({
    get: () => {
      const draft = drafts.draft
      const printBook = draft?.print_book

      if (!printBook && draft?.type !== 'PrintBook') return null
      if (!printBook?.settings?.documentFontSet) return documentFonts.find((font) => font.id === 0) || null

      return documentFonts.find((font) => font.id === printBook.settings.documentFontSet) || null
    },
    set: (fontSet: TPrintBookFont | null) => {
      const printBook = drafts.draft?.print_book

      if (!printBook?.settings || !fontSet) return

      printBook.settings.documentFontSet = fontSet.id
    },
  })

  const pageNumbering = computed<TPrintBookPageNumbering>({
    get: () => normalizePageNumbering(drafts.draft?.print_book?.settings?.pageNumbering),
    set: (numbering: TPrintBookPageNumbering) => {
      const printBook = drafts.draft?.print_book

      if (!printBook?.settings) return

      printBook.settings.pageNumbering = normalizePageNumbering(numbering)
    },
  })

  /**
   * Render the first page of the open print book editor to a JPEG and upload
   * it as the draft thumbnail. Returns the thumbnail file name, or null when
   * the editor for this draft is not on screen or the capture fails — the
   * caller then falls back to the backend generator.
   */
  function thumbnailSignatureKey(draftId: number): string {
    return `print-book-thumb-signature-${draftId}`
  }

  async function captureFirstPageThumbnail(draftId: number): Promise<TDraftThumbnail | null> {
    if (drafts.draft?.id !== draftId) return null

    const root = editor.getEditor()?.view.dom as HTMLElement | undefined
    if (!root) return null

    // only trust a stored signature while the thumbnail it describes still exists
    const previousSignature = drafts.draft?.thumbnail ? localStorage.getItem(thumbnailSignatureKey(draftId)) : null

    const currentFont = documentFont.value
    const render = await renderFirstPage({
      previousSignature,
      root,
      pageWidth: drafts.draft?.print_book?.page_width ?? 0,
      pageHeight: drafts.draft?.print_book?.page_height ?? 0,
      resolveFirstPage: tiptapPagesFirstPage,
      inlineImage: (url) => toDataURL(url),
      cssVars: {
        '--font-title': currentFont?.titleFont ?? 'inherit',
        '--font-body': currentFont?.bodyFont ?? 'inherit',
        '--font-size-h1': textSizes.value.h1 + 'px',
        '--font-size-h2': textSizes.value.h2 + 'px',
        '--font-size-h3': textSizes.value.h3 + 'px',
        '--font-size-h4': textSizes.value.h4 + 'px',
        '--font-size-body': textSizes.value.body + 'px',
      },
    })

    if (render.status === 'unavailable') return null

    if (render.status === 'unchanged') {
      const existing = drafts.draft
      return existing?.thumbnail
        ? { thumbnail: existing.thumbnail, thumbnail_src: existing.thumbnail_src ?? null }
        : null
    }

    const formData = new FormData()
    formData.append('image', render.blob, 'thumbnail.jpg')
    formData.append('draftId', String(draftId))

    const res = await fetch('/print_books/upload_thumbnail', { method: 'POST', body: formData })
    if (!res.ok) return null

    const body = (await res.json()) as { status?: string; thumbnail?: string; thumbnail_src?: string | null }
    if (body.status !== 'success' || !body.thumbnail) return null

    const generated: TDraftThumbnail = { thumbnail: body.thumbnail, thumbnail_src: body.thumbnail_src ?? null }
    localStorage.setItem(thumbnailSignatureKey(draftId), render.signature)
    if (drafts.draft?.id === draftId) {
      drafts.draft.thumbnail = generated.thumbnail
      drafts.draft.thumbnail_src = generated.thumbnail_src
    }
    return generated
  }

  async function generateThumbnail(draftIdParam?: number): Promise<TDraftThumbnail | null> {
    const draftId = draftIdParam ?? drafts.draft?.id
    if (!draftId) return null

    const inFlightGeneration = inFlightThumbnailGenerations.get(draftId)
    if (inFlightGeneration) {
      return inFlightGeneration
    }

    const generationPromise = (async () => {
      try {
        // capturing in the browser is far cheaper than the backend generator,
        // which renders the whole book through the remote PDF service
        const captured = await captureFirstPageThumbnail(draftId).catch(() => null)
        if (captured) return captured

        const res = await http.post<{ status: string; thumbnail?: string; thumbnail_src?: string | null }>(
          '/print_books/generate_thumbnail',
          { draftId },
        )
        return res?.thumbnail ? { thumbnail: res.thumbnail, thumbnail_src: res.thumbnail_src ?? null } : null
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to generate print book thumbnail')
        const bugsnag = (
          globalThis as typeof globalThis & {
            Bugsnag?: {
              notify: (
                error: Error,
                onError: (event: { addMetadata: (key: string, data: object) => void }) => void,
              ) => void
            }
          }
        ).Bugsnag
        bugsnag?.notify(error, (event: { addMetadata: (key: string, data: object) => void }) => {
          event.addMetadata('printBookThumbnail', {
            draftId,
            routeName: String(router.currentRoute.value.name ?? ''),
            routePath: router.currentRoute.value.path,
            rawError: String(err),
          })
        })
        console.error('Failed to generate thumbnail:', err)
        return null
      } finally {
        inFlightThumbnailGenerations.delete(draftId)
      }
    })()

    inFlightThumbnailGenerations.set(draftId, generationPromise)

    return generationPromise
  }

  function updateStepURL(printBookId?: number): void {
    const id = printBookId ?? drafts.draft?.print_book?.id
    if (!id) return

    const fullPath = router.currentRoute.value.fullPath
    if (!isValidPrintBookStepUrl(fullPath)) {
      return
    }

    stepUrlMap.value[id] = fullPath
  }

  async function validateStoredSteps(): Promise<void> {
    const items = Object.fromEntries(
      Object.entries(stepUrlMap.value).filter(([, url]) => isValidPrintBookStepUrl(url)),
    ) as TPrintBookStepMap

    if (Object.keys(items).length !== Object.keys(stepUrlMap.value).length) {
      stepUrlMap.value = items
    }

    if (!Object.keys(items).length) return

    try {
      const res = await http.post<Record<number, boolean>>('/print_books/check', { items })

      if (!res) return

      const updated = { ...items }

      Object.entries(res).forEach(([id, exists]) => {
        if (!exists) {
          delete updated[Number(id)]
        }
      })

      stepUrlMap.value = updated
    } catch (err) {
      console.error('Failed to validate stored print book steps:', err)
    }
  }

  function getStepURL(printBookId: number): string | null {
    validateStoredSteps()

    const url = stepUrlMap.value?.[printBookId]
    if (!isValidPrintBookStepUrl(url)) {
      if (stepUrlMap.value?.[printBookId]) {
        const updated = { ...stepUrlMap.value }
        delete updated[printBookId]
        stepUrlMap.value = updated
      }

      return null
    }

    return url
  }

  function forceRepaginate(editor: Editor) {
    const images = Array.from(editor.view.dom.querySelectorAll('img'))
    const editorHasFocus = editor.view.dom.contains(document.activeElement)

    requestAnimationFrame(() => {
      const tr = editor.state.tr.setMeta('unique-id-change', Date.now())
      editor.view.dispatch(tr)
    })

    Promise.all(images.map((img) => img.decode().catch(() => {}))).then(() => {
      const tr = editor.state.tr.setMeta('unique-id-change', Date.now())
      editor.view.dispatch(tr)
    })

    if (editorHasFocus) editor.commands.focus()
  }

  return {
    createFromDraft,
    dimensionOptions,
    selectedDimensionOption,
    selectedPagesDimension,
    setPageFormat,
    cancelPageGeneration,
    onSizeUnitChange,
    setCover,
    documentFont,
    textSizes,
    setTextSize,
    resetTextSizes,
    isTextSizesModified,
    pageNumbering,
    sizeUnit,
    initSizeUnit,
    showAllDimensions,
    sizeUnitOptions,
    getPagesCount,
    toDataURL,
    generateThumbnail,
    captureFirstPageThumbnail,
    updateStepURL,
    getStepURL,
    validateStoredSteps,
    forceRepaginate,
    getPDF,
    preparePDF,
    getCachedPages,
    pages,
  }
})
